import React from 'react';
import { Dialog } from '../../components/ui/Dialog';
import {
  CreditCard,
  Calendar,
  Percent,
  Tag,
  DollarSign,
  Banknote,
  Building,
  Users,
  Calculator,
  Edit
} from 'lucide-react';
import { SchemeDetailResponse, Scheme } from '../../services/scheme';
import { Button } from '../../components/ui/Button';

interface SchemeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  schemeData: SchemeDetailResponse | null;
  isLoading: boolean;
  onEdit?: (scheme: Scheme) => void;
  selectedScheme?: Scheme | null;
}

const SchemeDetailModal: React.FC<SchemeDetailModalProps> = ({
  isOpen,
  onClose,
  schemeData,
  isLoading,
  onEdit,
  selectedScheme
}) => {
  if (!schemeData && !isLoading) return null;

  const scheme = schemeData?.result?.data;

  // Format currency
  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Render lender badge with appropriate color
  const renderLenderBadge = (lender: string) => {
    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-2 py-1 mr-2 mb-2 text-sm font-medium rounded-full ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Scheme Details"
      size="lg"
    >
      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 animate-spin border-t-blue-500"></div>
        </div>
      ) : scheme ? (
        <div className="p-0">
          {/* Scheme Header */}
          <div className="flex items-center px-6 pt-4 pb-4 mb-4 border-b border-gray-200">
            <div className="flex justify-center items-center mr-3 w-12 h-12 text-xl font-bold text-white bg-indigo-600 rounded-full">
              {scheme.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800 truncate">{scheme.name}</h2>
                {onEdit && selectedScheme && (
                  <Button
                    onClick={() => onEdit(selectedScheme)}
                    variant="outline"
                    className="text-blue-600"
                    leftIcon={<Edit size={14} />}
                  >
                    Edit
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center mt-1 space-x-2 text-sm">
                <div className="flex items-center text-gray-500">
                  <Calendar size={14} className="mr-1" />
                  {scheme.grossTenure} months (Adv. EMI: {scheme.advancedEMI})
                </div>
                <div className="flex items-center text-gray-500">
                  <Percent size={14} className="mr-1" />
                  Processing: {scheme.processingRate}%
                </div>
                {scheme.interestBased && (
                  <div className="flex items-center text-gray-500">
                    <Percent size={14} className="mr-1" />
                    Interest: {scheme.annualInterest}% p.a.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 pb-6">
            <div className="space-y-4">
              {/* Scheme Basic Info */}
              {/* Scheme Basic Info */}
              <div className="p-5 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                  <div className="p-2 mr-3 bg-indigo-100 rounded-full">
                    <CreditCard className="text-indigo-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Scheme Information</h3>
                </div>

                <div className="grid grid-cols-1 gap-y-5 gap-x-6 md:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Name</p>
                    <p className="font-medium">{scheme.name}</p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Gross Tenure</p>
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.grossTenure} months</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Advanced EMI</p>
                    <div className="flex items-center">
                      <Banknote size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.advancedEMI}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Processing Rate</p>
                    <div className="flex items-center">
                      <Percent size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.processingRate}%</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Subvention Cost</p>
                    <div className="flex items-center">
                      <DollarSign size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.subventionCost}%</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Annual Interest</p>
                    <div className="flex items-center">
                      <Percent size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.annualInterest}%</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">GST</p>
                    <div className="flex items-center">
                      <Tag size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.GST}%</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Minimum Amount</p>
                    <div className="flex items-center">
                      <DollarSign size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{formatCurrency(scheme.min)}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Category</p>
                    <div className="flex items-center">
                      <Tag size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">
                        {scheme.category && scheme.category.length > 0
                          ? scheme.category.join(', ')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Sub Category</p>
                    <div className="flex items-center">
                      <Tag size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">
                        {scheme.subCategory && scheme.subCategory.length > 0
                          ? scheme.subCategory.join(', ')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Is Active</p>
                    <div className="flex items-center">
                      <Tag size={14} className="mr-1 text-gray-500" />
                      <p className={`font-medium ${scheme.isActive ? 'text-green-600' : 'text-red-500'}`}>
                        {scheme.isActive ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Calculate on Root Amount</p>
                    <div className="flex items-center">
                      <Calculator size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.calOnRootAmt ? 'Yes' : 'No'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Interest Based</p>
                    <div className="flex items-center">
                      <Percent size={14} className="mr-1 text-gray-500" />
                      <p className="font-medium">{scheme.interestBased ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
              </div>


              {/* Partners and Lenders */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Partners */}
                <div className="p-5 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                    <div className="p-2 mr-3 bg-blue-100 rounded-full">
                      <Building className="text-blue-600" size={18} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Partner Organizations</h3>
                  </div>

                  <div className="flex flex-wrap">
                    {scheme.partner && scheme.partner.length > 0 ? (
                      scheme.partner.map((partner, index) => (
                        <span key={index} className="px-2 py-1 mr-2 mb-2 text-sm font-medium text-blue-700 uppercase bg-blue-100 rounded-full">
                          {partner}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500">No partners assigned</p>
                    )}
                  </div>
                </div>

                {/* Lenders */}
                <div className="p-5 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                    <div className="p-2 mr-3 bg-green-100 rounded-full">
                      <Users className="text-green-600" size={18} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Lenders</h3>
                  </div>

                  <div className="flex flex-wrap">
                    {scheme.lender && scheme.lender.length > 0 ? (
                      scheme.lender.map((lender, index) => (
                        <React.Fragment key={index}>
                          {renderLenderBadge(lender)}
                        </React.Fragment>
                      ))
                    ) : (
                      <p className="text-gray-500">No lenders assigned</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500">
          Scheme information not available
        </div>
      )}
    </Dialog>
  );
};

export default SchemeDetailModal; 